"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getModifyCompensationPermission = exports.getModifySubsidyPermission = exports.PermissionsDynamic = void 0;
const constants_1 = require("@daytrip/constants");
const legacy_config_1 = require("@daytrip/legacy-config");
const legacy_enums_1 = require("@daytrip/legacy-enums");
const legacy_utils_1 = require("@daytrip/legacy-utils");
const utils_1 = require("@daytrip/utils");
exports.PermissionsDynamic = {
    // -- for myself (anybody's context) --
    "User_Myself:Read": ({ uid }, userId) => uid === userId,
    "UserPassword_Myself:Modify": ({ uid }, userId) => uid === userId,
    // -- for my company driver (company's context) --
    "User_MyCompanyDriver:Read": ({ uid }, driver) => uid === driver.driver.driversCompanyUserId,
    "User_SelfCancelTrip:Modify": ({ uid }, assignation) => assignation.ownerDriverId === uid,
    "Chargeback_ForNeverConfirmedOrder:Create": (_data, order, originTimezone) => {
        const departureInHours = (0, legacy_utils_1.calculateDepartInFromBookingTime)(order.departureAt, originTimezone, "hours");
        if ((0, utils_1.isUndefinedOrNull)(order.confirmedAt) &&
            (order.status === legacy_enums_1.OrderStatus.Cancelled || order.status === legacy_enums_1.OrderStatus.Declined) &&
            departureInHours > 0) {
            return true;
        }
        return false;
    },
    "Chargeback_Cancelled:Create": (_data, order, originTimezone) => {
        if (order.status !== legacy_enums_1.OrderStatus.Cancelled) {
            return false;
        }
        const departureInHours = (0, legacy_utils_1.calculateDepartInFromBookingTime)(order.departureAt, originTimezone, "hours");
        const hoursSinceCreation = (new Date().getTime() - new Date(order.createdAt).getTime()) / constants_1.HOUR_IN_MILLISECONDS;
        // for urgent orders we allow chargebacks in the first 3 hours after booking
        if (departureInHours < legacy_config_1.LAST_MINUTE_ORDER_THRESHOLD_IN_HOURS && hoursSinceCreation <= 3) {
            return true;
        }
        if (order.priceType === legacy_enums_1.PricingType.NonRefundable) {
            return false;
        }
        if (order.priceType === legacy_enums_1.PricingType.Flexible && !order.departureDateChangedLessThan24hUntilDeparture) {
            return departureInHours > 24;
        }
        if (order.priceType === legacy_enums_1.PricingType.SuperFlexible) {
            return departureInHours * 60 > 15;
        }
        return false;
    },
    "Chargeback_Surplus:Create": (_data, order, totalPrice, paidPrice, originTimezone, chargebackAmount) => {
        const departureInHours = (0, legacy_utils_1.calculateDepartInFromBookingTime)(order.departureAt, originTimezone, "hours");
        if (chargebackAmount) {
            if (departureInHours > 24 && paidPrice > totalPrice && paidPrice - totalPrice >= chargebackAmount) {
                return true;
            }
        }
        else if (departureInHours > 24 && paidPrice > totalPrice) {
            return true;
        }
        return false;
    },
    "Order_Myself:Read": ({ uid }, order) => order.userId === uid,
    "Orders_Myself:Read": ({ uid }, orders) => orders.every((order) => order.userId === uid),
    "Order_Myself:Update": ({ uid }, order) => order.userId === uid,
    "Pin_Myself:Read": ({ uid }, assignation, user) => !!(assignation.userId === uid || (user.driver && user.driver.driversCompanyUserId === uid)),
    "Payout_Pending:Fail": (_userInfo, payout) => payout && payout.status === legacy_enums_1.PayoutStatus.Pending,
    "Invoice_Myself:Read": ({ uid }, optionsUserIds) => optionsUserIds.length === 1 && optionsUserIds[0] === uid,
    "Feedback_Myself:Read": ({ uid }, order) => order.passengers.some((p) => p.userId === uid),
    "Feedback_Myself:Create": ({ uid }, order) => order.passengers.some((p) => p.userId === uid),
    "Feedback_Myself:Update": ({ uid }, order) => order.passengers.some((p) => p.userId === uid),
    "Order_Myself:Cancel": ({ uid }, order) => order.userId === uid,
    "Unavailability_Myself:Read": ({ uid }, unavailabilityOwnerUserId) => uid === unavailabilityOwnerUserId,
    "Unavailability_Myself:Update": ({ uid }, unavailabilityOwnerUserId) => uid === unavailabilityOwnerUserId,
    "OrderInAssignedCountries:Update": ({ roleDetails }, order) => {
        const rmCountryIds = roleDetails?.[legacy_enums_1.RolesEnum.SeniorRegionalManager]?.countryIds || [];
        return rmCountryIds.includes(order.originLocation.countryId);
    },
    "Assignation_Myself:Modify": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "AssignationsData_Myself:Read": ({ uid }, { userId }) => uid === userId,
    "BillingInformation_Myself:Modify": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "BillingInformation_Myself:SetActive": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "MangopayInformation_Myself:Update": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "DriversCompany_Myself:Read": ({ uid }, { userId }) => uid === userId,
    "DriversCompany_Myself:Modify": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "My_DriversCompany:Read": ({ uid }, user, companyUserId) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === companyUserId));
    },
    "User_Driver_Myself:Read": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "User_Driver_RM:Modify": ({ uid }, user, rm) => {
        return uid === rm.userId && !!(user.driver || user.driversCompany) && rm.countryIds.includes(user.countryId);
    },
    "User_Driver_Company:Modify": ({ uid }, user) => {
        return !!(user.driver && user.driver.driversCompanyUserId === uid);
    },
    "User_Driver_Myself:Update": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "DriverDocument_Myself:Modify": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "DriverBalance_Myself:Read": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "DriverVehicle_Myself:Update": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "DriverVehicleDocument_Myself:Modify": ({ uid }, user) => {
        return !!(uid === user._id || (user.driver && user.driver.driversCompanyUserId === uid));
    },
    "Payment_Myself:Create": ({ uid }, { userId }) => uid === userId,
    "Vehicle_Myself:Create": ({ uid }, { userId }) => uid === userId,
    "TravelAgent:Modify": (isTravelAgent) => Boolean(isTravelAgent),
};
const getModifySubsidyPermission = (subsidy) => {
    return subsidy && subsidy > legacy_config_1.HIGH_SUBSIDY_LIMIT ? "SubsidyOverLimit:Modify" : "Assignation:Modify";
};
exports.getModifySubsidyPermission = getModifySubsidyPermission;
const getModifyCompensationPermission = (compensation) => {
    return compensation && compensation > legacy_config_1.HIGH_COMPENSATION_LIMIT ? "CompensationOverLimit:Modify" : "Assignation:Modify";
};
exports.getModifyCompensationPermission = getModifyCompensationPermission;
//# sourceMappingURL=permissions.js.map